import { Platform } from 'react-native';

/**
 * Open one of the app's `/print/**` routes.
 *
 * On web this MUST be a real page load (`window.open`), never a client-side
 * `router.push`. Pushing a nested print route resolves to a different member of the
 * print group: the exterior-application print button pushed
 * `/print/exterior-application/<id>` and landed on `/print/reports`, which then
 * requested `reports/work-orders?status=undefined`, got a 400, and left the user
 * staring at a spinner that never resolved.
 *
 * A new tab is also the behaviour these pages are written for — they auto-fire
 * `window.print()` on load, so opening in place would hijack whatever screen the
 * user was on (and lose their spot in the inbox).
 *
 * Every other print button in the app already does this by hand; this centralises it
 * so the next one can't get it wrong.
 *
 * @param url    Absolute app path, e.g. `/print/exterior-application/<id>`.
 * @param navigate Fallback used off-web (or when there is no `window`), typically
 *                 `(u) => router.push(u as Href)`. Off-web that lands on app/print/_layout.tsx's
 *                 "Printing is available on the web app" screen — nothing is printed.
 */
export function openPrintRoute(url: string, navigate?: (url: string) => void): void {
	if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.open === 'function') {
		window.open(url, '_blank', 'noopener,noreferrer');
		return;
	}
	navigate?.(url);
}

/**
 * {@link openPrintRoute}, but saying whether it worked — for a caller that records the print
 * (e.g. marks invoices printed) and must not when the browser blocked the tab.
 *
 * Opened WITHOUT `noopener`: with it, `window.open` returns `null` whether or not the tab opened,
 * so a block is undetectable. The opener is cut right after instead — same isolation for a
 * same-origin print page.
 *
 * @returns `'tab'` opened, `'in-app'` navigated in-app (off-web: the print layout's "Printing is
 *          available on the web app" screen, so nothing was printed), `'blocked'` nothing opened.
 */
export function openPrintRouteChecked(url: string, navigate?: (url: string) => void): 'tab' | 'in-app' | 'blocked' {
	if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.open === 'function') {
		const tab = window.open(url, '_blank');
		if (!tab) return 'blocked';
		tab.opener = null;
		return 'tab';
	}
	navigate?.(url);
	return 'in-app';
}
